import { ReactComponent as InfoIcon } from "assets/products/shared/icons/info.svg";
import { ReactComponent as BikeIcon } from "assets/products/shared/icons/bike.svg";
import { ReactComponent as AirplaneIcon } from "assets/products/shared/icons/airplane.svg";
import { ReactComponent as HeartIcon } from "assets/products/shared/icons/heart.svg";
import { ReactComponent as HospitalIcon } from "assets/products/shared/icons/hospital.svg";
import { ReactComponent as EmergencyIcon } from "assets/products/shared/icons/emergency.svg";
import { ReactComponent as PlusIcon } from "assets/products/shared/icons/plus.svg";
import ModalTemplate from "../modal-template/ModalTemplate";
import { useQuote } from "hooks/useQuote";
import { formatUSCurrency } from "utils/formatUSCurrency";
import { getSelectedBenefit } from "utils/mltaQuoteUtils";

const CoverageDetailsModal = () => {
    // ========== Hooks ==========
    const { quoteData } = useQuote();

    // ========== Computed Values ==========
    const subTitleClassName = "font-medium text-xs leading-5 text-base-content mb-1";
    const descriptionClassName = "font-semibold text-sm leading-5 text-base-content pl-6";

    const selectedTier = quoteData?.selectedTier;
    const selectedType = quoteData?.selectedCoverageType;
    const selectedPremium = quoteData?.selectedCoveragePremium;

    const selectedBenefitOption = getSelectedBenefit(quoteData?.mltaBenefits ?? [], selectedTier ?? "");

    const formatCurrency = (value: number | undefined) => {
        if (value === undefined) return "$0";
        return formatUSCurrency(value, "auto");
    };

    const formatCurrencyWithSuffix = (value: number | undefined, suffix: string) => {
        if (value === undefined) return `$0/${suffix}`;
        return `${formatUSCurrency(value, "auto")}/${suffix}`;
    };

    // Loss of Life section
    const lossOfLifeVehicle = selectedBenefitOption?.common ?? 0;
    const lossOfLifeAirline = selectedBenefitOption?.airline ?? 0;
    const lossOfLifeOther = selectedBenefitOption?.lossOfLife ?? 0;
    // Vehicle Accident section
    const vehicleHospital = selectedBenefitOption?.hospital ?? 0;
    const vehicleEmergency = selectedBenefitOption?.emergencyRoom ?? 0;
    // Other Accidents section
    const otherAccident = selectedBenefitOption?.allAccident ?? 0;

    const monthlyPremium =
        selectedPremium ?? (selectedType === "Family" ? selectedBenefitOption?.family : selectedBenefitOption?.individual) ?? 0;

    const tierTitle = selectedTier ? selectedTier.charAt(0).toUpperCase() + selectedTier.slice(1).toLowerCase() : "Superior";
    const coverageTypeTitle = selectedType === "Family" ? "Family Coverage" : "Individual Coverage";

    return (
        <ModalTemplate
            trigger={
                <button className="group btn btn-outline bg-base-content hover:bg-white w-[182px] h-[40px] min-h-[40px] max-h-[40px] py-0 text-white hover:text-secondary-content font-bold text-sm leading-none flex items-center justify-center gap-2 transition-colors">
                    <InfoIcon className="w-6 h-6 [&_path]:stroke-[1.5] brightness-0 invert group-hover:brightness-100 group-hover:invert-0 [&_path]:group-hover:stroke-secondary-content transition-colors" />
                    Coverage Details
                </button>
            }
            modalId="coverage-details-modal"
            className="p-0 tablet:max-w-[385px] max-w-[370px]"
        >
            <div className="flex flex-col max-h-[calc(100vh-8rem)] tablet:max-h-none">
                <div className="flex flex-col flex-shrink-0 px-6 pt-4">
                    <div>
                        <p className="font-medium text-sm leading-5 text-base-content">
                            Member Loyalty<sup>®</sup> Accident Insurance
                        </p>
                        <p className="font-bold text-lg leading-5 text-base-content">
                            {tierTitle} {coverageTypeTitle}
                        </p>
                    </div>
                    <div className="border-b-2 border-base-content/10 mt-4 -mx-6 tablet:hidden"></div>
                </div>
                <div className="flex flex-col gap-2 p-6 overflow-y-auto tablet:overflow-visible tablet:pt-[16px]">
                    <div>
                        <p className={subTitleClassName}>Loss of Life</p>

                        <div className="font-medium text-sm leading-5 text-base-content flex gap-1">
                            <BikeIcon className="w-5 h-5" />
                            <p className="text-base-content font-bold text-sm leading-5">{formatCurrency(lossOfLifeVehicle)}</p>
                        </div>
                        <p className={descriptionClassName}>Vehicle related accident</p>

                        <div className="flex flex-col">
                            <div className="font-medium text-sm leading-5 text-base-content flex gap-1">
                                <AirplaneIcon className="w-5 h-5" />
                                <p className="text-base-content font-bold text-sm leading-5">
                                    {formatCurrency(lossOfLifeAirline)}
                                </p>
                            </div>
                            <p className={descriptionClassName}>Airline accident</p>
                        </div>

                        <div className="flex flex-col">
                            <div className="font-medium text-sm leading-5 text-base-content flex gap-1">
                                <HeartIcon className="w-5 h-5" />
                                <p className="text-base-content font-bold text-sm leading-5">
                                    {formatCurrency(lossOfLifeOther)}
                                </p>
                            </div>
                            <p className={descriptionClassName}>Any other accident</p>
                        </div>
                    </div>
                    <div>
                        <p className={subTitleClassName}>Vehicle Accident</p>

                        <div className="font-medium text-sm leading-5 text-base-content flex gap-1">
                            <HospitalIcon className="w-4 h-4" />
                            <p className="text-base-content font-bold text-sm leading-5">
                                {formatCurrencyWithSuffix(vehicleHospital, "day")}
                            </p>
                        </div>
                        <p className={descriptionClassName}>Hospital stay or recuperation at home</p>

                        <div className="flex flex-col">
                            <div className="font-medium text-sm leading-5 text-base-content flex gap-1">
                                <EmergencyIcon className="w-4 h-4" />
                                <p className="text-base-content font-bold text-sm leading-5">
                                    {formatCurrencyWithSuffix(vehicleEmergency, "year")}
                                </p>
                            </div>
                            <p className={descriptionClassName}>Vehicle related emergency or urgent care visit</p>
                        </div>
                    </div>
                    <div>
                        <p className={subTitleClassName}>Other Accidents</p>

                        <div className="font-medium text-sm leading-5 text-base-content flex gap-1">
                            <HospitalIcon className="w-4 h-4" />
                            <p className="text-base-content font-bold text-sm leading-5">
                                {formatCurrencyWithSuffix(otherAccident, "day")}
                            </p>
                        </div>
                        <p className={descriptionClassName}>Hospitalization and recovery</p>

                        <div className="flex flex-col">
                            <div className="font-medium text-sm leading-5 text-base-content flex gap-1">
                                <EmergencyIcon className="w-4 h-4" />
                                <p className="text-base-content font-bold text-sm leading-5">
                                    {formatCurrencyWithSuffix(otherAccident, "year")}
                                </p>
                            </div>
                            <p className={descriptionClassName}>Emergency or urgent care visit</p>
                        </div>
                    </div>
                    {selectedType === "Family" && (
                        <div>
                            <p className={subTitleClassName}>Family Coverage</p>
                            <div className="flex flex-col gap-2">
                                <div className="flex items-start gap-2">
                                    <PlusIcon className="w-3 h-3 flex-shrink-0 mt-[7px] [&_path]:stroke-[1]" />
                                    <div className="flex flex-col">
                                        <p className="font-bold text-sm leading-5 text-base-content">Spouse's Coverage</p>
                                        <p className="font-medium text-sm leading-5 text-base-content">
                                            Your spouse receives 60% of all your benefits
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-2">
                                    <PlusIcon className="w-3 h-3 flex-shrink-0 mt-[7px] [&_path]:stroke-[1]" />
                                    <div className="flex flex-col">
                                        <p className="font-bold text-sm leading-5 text-base-content">Children's Coverage</p>
                                        <p className="font-medium text-sm leading-5 text-base-content">
                                            Your children receive 20% of all your benefits
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div className="flex flex-col rounded-b-2xl overflow-hidden flex-shrink-0">
                    <div className="bg-base-content px-6 py-4 text-center">
                        <p className="text-white font-medium text-sm leading-5">{coverageTypeTitle}</p>
                        <p className="text-white font-bold text-lg leading-6 mt-1">{formatCurrency(monthlyPremium)}/month</p>
                    </div>
                </div>
            </div>
        </ModalTemplate>
    );
};

export default CoverageDetailsModal;
